import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { ActivityIndicator } from "components/activity-indicator"
import { ShuffledVideosByTag } from "components/videos/ShuffledVideosByTag"
import ConventionTag from "models/convention-api/v1/ConventionTag"
import React, { useCallback, useEffect, useRef, useState } from "react"

const useStyles = makeStyles()(
  (theme) => ({
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      width: "100%",
    },
    loadingIndicatorContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(2),
      width: "100%",
    },
  })
)
interface ShuffledVideosByTagsProps {
  fallbackData: ConventionTag[]
  onLoad: () => void
}
const PAGE_LIMIT = 10

export const ShuffledVideosByTags = (props: ShuffledVideosByTagsProps) => {
  const { fallbackData, onLoad } = props
  const { classes } = useStyles()

  const tags = ConventionTag.useMore({
    limit: PAGE_LIMIT,
    sort: "shuffled_position",
    filter: {
      tagged: "video",
    },
    fallbackData,
  })

  const [loadedTagLists, setLoadedTagLists] = useState<Record<string, number[]>>({})
  const getLoadedTagListIndex = (tagIndex: number) => Math.floor(tagIndex / PAGE_LIMIT)
  const getLoadedTagList = (tagIndex: number) => {
    const loadedTagListIndex = getLoadedTagListIndex(tagIndex)
    return loadedTagLists[loadedTagListIndex] ?? []
  }

  const isTagVisible = (tagIndex: number) => {
    const tagListIndex = getLoadedTagListIndex(tagIndex)
    const tagListSize = tags.size - 1
    const list = getLoadedTagList(tagIndex)

    if (tagListIndex === tagListSize)
      return list.length >= (tags.data?.length ?? 0) - PAGE_LIMIT * tagListSize

    return list.length >= PAGE_LIMIT
  }

  const addLoadedTag = (tagIndex: number) =>
    setLoadedTagLists((prevState) => {
      const tagListIndex = getLoadedTagListIndex(tagIndex)
      const tagList = prevState[tagListIndex] ?? []
      return {
        ...prevState,
        [tagListIndex]: [...tagList, tagIndex],
      }
    })

  const isLastTagLoaded = isTagVisible(tags.data?.length - 1)

  const observer = useRef<IntersectionObserver>(null)
  const loadingIndicatorContainerRef = useCallback(
    (node: HTMLElement | null) => {
      if (tags.isLoading) return
      if (observer.current) observer.current.disconnect()
      // @ts-ignore
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && tags.hasMore && isLastTagLoaded) tags.loadMore()
      })
      if (node) {
        observer.current.observe(node)
      }
    },
    [tags.isLoading, tags.hasMore, isLastTagLoaded],
  )

  useEffect(() => {
    if (tags.data !== undefined && tags.data?.length === 0) return onLoad()
    if (isTagVisible(tags.data?.length > PAGE_LIMIT ? PAGE_LIMIT - 1 : tags.data?.length - 1))
      return onLoad()
  }, [tags.data, tags.isLoading, loadedTagLists, isTagVisible])

  return (
    <Box className={classes.container}>
      {tags.data?.map((tag, index) => {
        const isVisible = isTagVisible(index)
        return (
          <ShuffledVideosByTag
            key={tag.id}
            tag={tag}
            isVisible={isVisible}
            onLoad={() => !isVisible && addLoadedTag(index)}
          />
        )
      })}
      {(tags.hasMore || tags.isLoading || !isLastTagLoaded) && (
        <Box
          className={classes.loadingIndicatorContainer}
          {...{ ref: loadingIndicatorContainerRef }}>
          <ActivityIndicator />
        </Box>
      )}
    </Box>
  )
}
